/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import React, { FC, useState, useEffect, useMemo } from "react";
import { DIALOG_ENTRY_STATE, IDialogEntry } from "../../../dialogs/DialogService";

export interface IDialogHostProps {
	entry: IDialogEntry;
}

export const DialogHost: FC<IDialogHostProps> = ({ entry }) => {
	const [ state, setState ] = useState(entry.state);

	useEffect(() => {
		const handleStateChange = () => setState(entry.state);
		onEvent(entry.onStateChange, handleStateChange);

		return () => {
			offEvent(entry.onStateChange, handleStateChange);
		};
	}, [ entry ]);

	const dialogComponent = useMemo(
		() => ({
			component: entry.instance.getComponent()
		}),
		[ entry.instance ]
	);

	const baseClass = BEM("edt-dialog", null, {
		"is-opened": state === DIALOG_ENTRY_STATE.OPENED
	});

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("overlay").toClassName()}></div>
			<dialogComponent.component dialog={entry.instance} />
		</div>
	);
};
